import { Copy, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import Button from '../../../components/Button';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import LessonFormTabs from './LessonFormTabs';
import type { AdminLearningService } from '../services/learningService';
import type { CreateLessonData, LearningLesson, LearningSection, LessonPage, QuestionOption } from '../services/learningTypes';
import { isQuestionPage } from '../services/learningTypes';
import { cleanIdForStorage } from '../../../utils/idValidation';
import LessonDetailsForm from './LessonDetailsForm';
import LessonPagesManager from './LessonPagesManager';

interface LessonFormProps {
    section: LearningSection;
    lesson?: LearningLesson | null;
    service: AdminLearningService;
    onSave: () => void;
    onCancel: () => void;
    initialLessonData?: Partial<LearningLesson>;
    initialPages?: LessonPage[];
}

export default function LessonForm({ section, lesson, service, onSave, onCancel, initialLessonData, initialPages }: LessonFormProps) {
    const [formData, setFormData] = useState<CreateLessonData>({
        title: '',
        imageSrc: '',
        description: '',
        isFree: true,
        timeMinutes: 5,
        type: 'lesson',
        status: 'draft',
        order: 1,
        color: '#A8E6CF'
    });
    const [pages, setPages] = useState<LessonPage[]>([]);
    const [currentLesson, setCurrentLesson] = useState<LearningLesson | null>(lesson || null);

    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'details' | 'pages'>('details');
    const [loadingPages, setLoadingPages] = useState(false);
    const [pageSaveStates, setPageSaveStates] = useState<Record<number, 'saved' | 'unsaved' | 'saving' | 'error'>>({});
    const [lessonSaveStatus, setLessonSaveStatus] = useState<'saved' | 'unsaved' | 'saving' | 'error' | null>(null);
    const [duplicateStatus, setDuplicateStatus] = useState<'idle' | 'duplicating' | 'success' | 'error'>('idle');
    const [deleteConfirmation, setDeleteConfirmation] = useState<{
        isOpen: boolean;
        pageIndex: number | null;
        isDeleting: boolean;
    }>({
        isOpen: false,
        pageIndex: null,
        isDeleting: false
    });

    const loadLessonPages = useCallback(async () => {
        if (!currentLesson) return;

        try {
            setLoadingPages(true);
            const pagesData = await service.getPagesInLesson(section.uid, currentLesson.uid);
            setPages(pagesData);
        } catch (err) {
            console.error('Error loading lesson pages:', err);
        } finally {
            setLoadingPages(false);
        }
    }, [currentLesson, section.uid, service]);

    useEffect(() => {
        if (lesson) {
            setFormData({
                id: lesson.uid,
                title: lesson.title,
                imageSrc: lesson.imageSrc,
                description: lesson.description,
                isFree: lesson.isFree,
                timeMinutes: lesson.timeMinutes,
                type: lesson.type,
                status: lesson.status,
                order: lesson.order || 1,
                color: lesson.color
            });
            loadLessonPages();
        } else if (initialLessonData) {
            // Use initial lesson data from pasted content
            setFormData(prev => ({
                ...prev,
                id: initialLessonData.uid,
                title: initialLessonData.title || prev.title,
                imageSrc: initialLessonData.imageSrc || prev.imageSrc,
                description: initialLessonData.description || prev.description,
                isFree: initialLessonData.isFree !== undefined ? initialLessonData.isFree : prev.isFree,
                timeMinutes: initialLessonData.timeMinutes || prev.timeMinutes,
                type: initialLessonData.type || prev.type,
                status: initialLessonData.status || prev.status,
                order: initialLessonData.order || prev.order,
                color: initialLessonData.color || prev.color
            }));

            // Set initial pages if provided
            if (initialPages && initialPages.length > 0) {
                setPages(initialPages);
                // Mark all pages as unsaved
                const pageStates: Record<number, 'unsaved'> = {};
                initialPages.forEach((_, index) => {
                    pageStates[index] = 'unsaved';
                });
                setPageSaveStates(pageStates);
            }
        } else {
            // Get next order for new lessons
            const getNextOrder = async () => {
                try {
                    const lessons = await service.getLessonsInSection(section.uid);
                    const maxOrder = Math.max(...lessons.map(l => l.order || 0), 0);
                    setFormData(prev => ({ ...prev, order: maxOrder + 1 }));
                } catch (err) {
                    console.error('Error getting next order:', err);
                }
            };
            getNextOrder();
        }
    }, [lesson, section.uid, loadLessonPages, initialLessonData, initialPages]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLessonSaveStatus('saving');
        setError(null);

        // Basic validation
        if (!formData.title.trim()) {
            setError('Title is required');
            setLessonSaveStatus('error');
            return;
        }
        if (!formData.description.trim()) {
            setError('Description is required');
            setLessonSaveStatus('error');
            return;
        }

        try {
            // Clean the ID for storage
            const cleanedFormData = {
                ...formData,
                id: formData.id ? cleanIdForStorage(formData.id) : formData.id
            };

            if (currentLesson) {
                // Update existing lesson (lesson fields only)
                await service.updateLesson(section.uid, currentLesson.uid, cleanedFormData);
            } else {
                // Create new lesson (lesson fields only)
                const newLessonId = await service.createLesson(section.uid, cleanedFormData);

                // Fetch the newly created lesson to transition to edit mode
                const newLesson = await service.getLesson(section.uid, newLessonId);
                if (newLesson) {
                    setCurrentLesson(newLesson);
                    // Update form data with the actual lesson ID
                    setFormData(prev => ({ ...prev, id: newLessonId }));

                    // If there are pages in memory, save them automatically
                    if (pages.length > 0) {
                        setLessonSaveStatus('saved');

                        // Save all pages that don't have an originalUid (new pages)
                        for (let i = 0; i < pages.length; i++) {
                            const page = pages[i];
                            if (!page.originalUid) {
                                // Mark page as saving
                                setPageSaveStates(prev => ({ ...prev, [i]: 'saving' }));

                                try {
                                    // Clean the page data for storage
                                    const cleanedPage = {
                                        ...page,
                                        uid: page.uid ? cleanIdForStorage(page.uid) : page.uid
                                    };

                                    // Create the page
                                    const pageId = await service.createPage(section.uid, newLessonId, cleanedPage);

                                    // Update the page with the new ID
                                    setPages(prev => prev.map((p, idx) =>
                                        idx === i ? { ...p, uid: pageId, originalUid: pageId } : p
                                    ));

                                    // Mark page as saved
                                    setPageSaveStates(prev => ({ ...prev, [i]: 'saved' }));
                                } catch (pageErr) {
                                    console.error(`Error saving page ${i}:`, pageErr);
                                    setPageSaveStates(prev => ({ ...prev, [i]: 'error' }));
                                }
                            }
                        }

                        // Clear page saved states after 3 seconds
                        setTimeout(() => {
                            setPageSaveStates({});
                        }, 1000);
                    }
                } else {
                    throw new Error('Failed to fetch newly created lesson');
                }
            }

            setLessonSaveStatus('saved');

            setLessonSaveStatus(null);
            onSave(); // Close popup and refresh the lesson list

        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save lesson');
            setLessonSaveStatus('error');
        }
    };

    const handleFormDataChange = (field: keyof CreateLessonData, value: string | number | boolean) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Mark lesson as having unsaved changes
        if (currentLesson && lessonSaveStatus !== 'unsaved') {
            setLessonSaveStatus('unsaved');
        }
    };

    const handleDuplicate = async () => {
        if (!currentLesson) return;

        setDuplicateStatus('duplicating');
        setError(null);

        try {
            await service.duplicateLesson(section.uid, currentLesson.uid);
            setDuplicateStatus('success');

            // Reset status after 2 seconds and close the popup
            setTimeout(() => {
                setDuplicateStatus('idle');
                onSave(); // Close popup and refresh the lesson list
            }, 2000);

        } catch (err) {
            console.error('Error duplicating lesson:', err);
            setError(err instanceof Error ? err.message : 'Failed to duplicate lesson');
            setDuplicateStatus('error');

            // Reset error status after 3 seconds
            setTimeout(() => {
                setDuplicateStatus('idle');
            }, 3000);
        }
    };

    const handleAddPage = (pageType: 'page' | 'question') => {
        const basePageData = {
            uid: '',
            content: pageType === 'question' ? '## \n\n ```question\nQuestion\n```' : '## \n\n ```learning\nLearning\n```',
            buttonText: pageType === 'question' ? 'Submit Answer' : 'Continue',
            order: pages.length + 1,
            type: pageType
        };

        let newPage: LessonPage;
        if (pageType === 'question') {
            newPage = {
                ...basePageData,
                type: 'question',
                aiFriendlyQuestion: '',
                shouldSaveAnswer: false,
                options: [],
                correctOptionIds: []
                // Note: no originalUid for new pages
            };
        } else {
            newPage = {
                ...basePageData,
                type: 'page'
                // Note: no originalUid for new pages
            };
        }

        setPages(prev => [...prev, newPage]);
        // Mark new page as unsaved
        setPageSaveStates(prev => ({
            ...prev,
            [pages.length]: 'unsaved'
        }));
    };

    const handleUpdatePage = (index: number, field: string, value: string | number | boolean | QuestionOption[] | string[]) => {
        setPages(prev => prev.map((page, i) =>
            i === index ? { ...page, [field]: value } : page
        ));
        // Mark page as unsaved when content changes
        setPageSaveStates(prev => ({
            ...prev,
            [index]: 'unsaved'
        }));
    };

    const handleDeletePage = (index: number) => {
        setDeleteConfirmation({
            isOpen: true,
            pageIndex: index,
            isDeleting: false
        });
    };

    const confirmDeletePage = async () => {
        const { pageIndex } = deleteConfirmation;
        if (pageIndex === null) return;

        setDeleteConfirmation(prev => ({ ...prev, isDeleting: true }));

        try {
            const page = pages[pageIndex];

            // If the page has an originalUid, it exists in the database and needs to be deleted
            if (page?.originalUid && currentLesson) {
                await service.deletePage(section.uid, currentLesson.uid, page.originalUid);
            }

            // Remove the page from local state
            setPages(prev => prev.filter((_, i) => i !== pageIndex));

            // Remove save state for deleted page and adjust indices for remaining pages
            setPageSaveStates(prev => {
                const newStates = { ...prev };
                delete newStates[pageIndex];

                // Shift indices for pages after the deleted one
                const adjustedStates: Record<number, typeof prev[number]> = {};
                Object.keys(newStates).forEach(key => {
                    const pageIndexKey = parseInt(key);
                    if (pageIndexKey > pageIndex) {
                        adjustedStates[pageIndexKey - 1] = newStates[pageIndexKey];
                    } else if (pageIndexKey < pageIndex) {
                        adjustedStates[pageIndexKey] = newStates[pageIndexKey];
                    }
                });

                return adjustedStates;
            });

            // Close the confirmation dialog
            setDeleteConfirmation({
                isOpen: false,
                pageIndex: null,
                isDeleting: false
            });

        } catch (err) {
            console.error('Error deleting page:', err);
            setError(err instanceof Error ? err.message : 'Failed to delete page');
            setDeleteConfirmation(prev => ({ ...prev, isDeleting: false }));
        }
    };

    const cancelDeletePage = () => {
        setDeleteConfirmation({
            isOpen: false,
            pageIndex: null,
            isDeleting: false
        });
    };

    const handleSavePage = async (index: number) => {
        if (!currentLesson) {
            setError('Cannot save page: lesson not yet created. Please save the lesson first.');
            return;
        }

        const page = pages[index];
        if (!page) return;

        // Validate question page with shouldSaveAnswer enabled
        if (isQuestionPage(page) && page.shouldSaveAnswer) {
            if (!page.aiFriendlyQuestion?.trim()) {
                setError('AI-friendly question is required when saving answers is enabled.');
                setPageSaveStates(prev => ({ ...prev, [index]: 'error' }));
                return;
            }

            // Check if all options have AI-friendly text
            const missingAiFriendlyText = page.options.some(option => !option.aiFriendlyText?.trim());
            if (missingAiFriendlyText) {
                setError('All answer options must have AI-friendly text when saving answers is enabled.');
                setPageSaveStates(prev => ({ ...prev, [index]: 'error' }));
                return;
            }
        }

        setPageSaveStates(prev => ({ ...prev, [index]: 'saving' }));

        try {
            if (page.originalUid) {
                // Clean the page data for storage
                const cleanedPage = {
                    ...page,
                    uid: page.uid ? cleanIdForStorage(page.uid) : page.uid
                };

                // Update existing page using the original database ID
                await service.updatePage(section.uid, currentLesson.uid, page.originalUid, cleanedPage);

                // If the user changed the ID, we need to handle the ID change
                if (cleanedPage.uid !== page.originalUid && cleanedPage.uid) {
                    // Delete the old page and create a new one with the new ID
                    await service.deletePage(section.uid, currentLesson.uid, page.originalUid);
                    const newPageId = await service.createPage(section.uid, currentLesson.uid, cleanedPage);

                    // Update the page with the new database ID
                    setPages(prev => prev.map((p, i) =>
                        i === index ? { ...p, uid: newPageId, originalUid: newPageId } : p
                    ));
                }
            } else {
                // Clean the page data for storage
                const cleanedPage = {
                    ...page,
                    uid: page.uid ? cleanIdForStorage(page.uid) : page.uid
                };

                // Create new page and update the pages state with the returned ID
                const pageId = await service.createPage(section.uid, currentLesson.uid, cleanedPage);

                // Update the page with the new ID
                setPages(prev => prev.map((p, i) =>
                    i === index ? { ...p, uid: pageId, originalUid: pageId } : p
                ));
            }

            setPageSaveStates(prev => ({ ...prev, [index]: 'saved' }));

            // Clear saved status after 3 seconds
            setTimeout(() => {
                setPageSaveStates(prev => {
                    const newStates = { ...prev };
                    if (newStates[index] === 'saved') {
                        delete newStates[index];
                    }
                    return newStates;
                });
            }, 3000);

        } catch (err) {
            console.error('Error saving page:', err);
            setPageSaveStates(prev => ({ ...prev, [index]: 'error' }));
            setError(err instanceof Error ? err.message : 'Failed to save page');
        }
    };

    const handleConvertPageType = (index: number) => {
        const page = pages[index];
        if (!page) return;

        let convertedPage: LessonPage;

        if (isQuestionPage(page)) {
            // Convert question page to regular page
            convertedPage = {
                uid: page.uid,
                originalUid: page.originalUid,
                content: page.content.replace(/```question\s*\n\s*Question\s*\n\s*```/g, '```learning\nLearning\n```'),
                buttonText: 'Continue',
                order: page.order,
                type: 'page'
            };
        } else {
            // Convert regular page to question page
            convertedPage = {
                uid: page.uid,
                originalUid: page.originalUid,
                content: page.content.replace(/```learning\s*\n\s*Learning\s*\n\s*```/g, '```question\nQuestion\n```'),
                buttonText: 'Submit Answer',
                order: page.order,
                type: 'question',
                shouldSaveAnswer: false,
                options: [],
                correctOptionIds: []
            };
        }

        // Update the page in state
        setPages(prev => prev.map((p, i) => (i === index ? convertedPage : p)));

        // Mark page as unsaved
        setPageSaveStates(prev => ({
            ...prev,
            [index]: 'unsaved'
        }));
    };

    return (
        <div className="fixed inset-0 bg-gray-600/50 overflow-y-auto h-full w-full z-50 px-4 py-6 sm:py-10">
            <div className="relative mx-auto p-0 border w-full max-w-4xl shadow-lg rounded-md bg-white">
                {/* Header */}
                <div className="flex items-center justify-between gap-2 p-4 sm:p-6 border-b border-gray-200">
                    <div className="min-w-0">
                        <h3 className="text-lg font-medium text-gray-900 break-words">
                            {currentLesson ? 'Edit Lesson: ' + formData.title : 'Create New Lesson'}
                        </h3>
                        <p className="text-sm text-gray-500">Section: {section.title}</p>
                    </div>
                    <Button
                        onClick={onCancel}
                        variant="ghost"
                        icon={<X size={24} />}
                    />
                </div>

                {/* Tabs */}
                <LessonFormTabs
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                    pagesCount={pages.length}
                />

                {/* Form Content */}
                <form onSubmit={handleSubmit} className="p-4 sm:p-6">
                    {error && (
                        <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
                            <div className="text-sm text-red-700">{error}</div>
                        </div>
                    )}

                    {/* Show success message for duplication */}
                    {duplicateStatus === 'success' && !error && (
                        <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-3">
                            <div className="text-sm text-green-700">
                                Lesson successfully duplicated! Closing popup...
                            </div>
                        </div>
                    )}

                    {/* Details Tab */}
                    {activeTab === 'details' && (
                        <div>
                            {lessonSaveStatus === 'unsaved' && (
                                <div className="mb-4 bg-orange-50 border border-orange-200 rounded-md p-3">
                                    <div className="text-sm text-orange-700">
                                        You have unsaved changes to the lesson details.
                                    </div>
                                </div>
                            )}
                            <LessonDetailsForm
                                formData={formData}
                                onChange={handleFormDataChange}
                            />
                        </div>
                    )}

                    {/* Pages Tab */}
                    {activeTab === 'pages' && (
                        <LessonPagesManager
                            pages={pages}
                            loading={loadingPages}
                            onAddPage={handleAddPage}
                            onUpdatePage={handleUpdatePage}
                            onDeletePage={handleDeletePage}
                            onSavePage={handleSavePage}
                            onConvertPageType={handleConvertPageType}
                            pageSaveStates={pageSaveStates}
                        />
                    )}

                    {/* Buttons */}
                    <div className="flex flex-wrap sm:flex-nowrap gap-3 mt-6 pt-6 border-t border-gray-200">
                        <Button
                            type="button"
                            onClick={onCancel}
                            variant="secondary"
                            fullWidth
                            disabled={lessonSaveStatus === 'saving' || duplicateStatus === 'duplicating'}
                        >
                            Cancel
                        </Button>

                        {/* Show duplicate button only when editing an existing lesson */}
                        {currentLesson && (
                            <Button
                                type="button"
                                onClick={handleDuplicate}
                                variant={
                                    duplicateStatus === 'success' ? 'success' :
                                        duplicateStatus === 'error' ? 'danger' :
                                            'secondary'
                                }
                                icon={<Copy size={16} />}
                                loading={duplicateStatus === 'duplicating'}
                                disabled={
                                    duplicateStatus === 'duplicating' ||
                                    duplicateStatus === 'success' ||
                                    lessonSaveStatus === 'saving'
                                }
                            >
                                {duplicateStatus === 'duplicating' ? 'Duplicating...' :
                                    duplicateStatus === 'success' ? 'Duplicated!' :
                                        duplicateStatus === 'error' ? 'Retry Duplicate' :
                                            'Duplicate'}
                            </Button>
                        )}

                        {/* Only show Update/Create Lesson button on details tab */}
                        {activeTab === 'details' && (
                            <Button
                                type="submit"
                                variant={
                                    lessonSaveStatus === 'saved' ? 'success' :
                                        lessonSaveStatus === 'error' ? 'danger' :
                                            'primary'
                                }
                                fullWidth
                                loading={lessonSaveStatus === 'saving'}
                                disabled={
                                    lessonSaveStatus === 'saving' ||
                                    lessonSaveStatus === 'saved' ||
                                    duplicateStatus === 'duplicating'
                                }
                            >
                                {lessonSaveStatus === 'saving' ? 'Saving...' :
                                    lessonSaveStatus === 'saved' ? 'Lesson Saved' :
                                        lessonSaveStatus === 'error' ? 'Retry Save' :
                                            (currentLesson ? 'Update Lesson' : 'Create Lesson')}
                            </Button>
                        )}
                    </div>
                </form>
            </div>

            {/* Delete Confirmation Dialog */}
            <ConfirmationDialog
                isOpen={deleteConfirmation.isOpen}
                title="Delete Page"
                message={`Are you sure you want to delete page ${(deleteConfirmation.pageIndex ?? 0) + 1}? This action cannot be undone.`}
                confirmText="Delete Page"
                cancelText="Cancel"
                confirmVariant="danger"
                onConfirm={confirmDeletePage}
                onCancel={cancelDeletePage}
                loading={deleteConfirmation.isDeleting}
            />
        </div>
    );
} 